import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { SessionService } from '../Services/session.service';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError, first, map } from 'rxjs/operators';
import { IdUploadError, IdUploadService, SidesForDocType, UploadMethod } from './id-upload.service';
import { WebcamImage } from 'ngx-webcam';
import { HeadshotUploadService } from '../headshot-upload/headshot-upload.service';
import { dashboardRoute, headshotUploadRoute, idUploadRoute } from '../route-names';
import { ApiService, headshotUploadAPI, idUploadAPI } from '../api/api.service';
import { MteService, arrayBufferToBase64 } from '../Services/mte.service';

@Component({
    selector: 'app-id-upload-modal',
    templateUrl: './id-upload-modal.component.html',
    standalone: false
})
export class IdUploadModalComponent implements OnInit {
  @Input() isOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @Input() file?: BehaviorSubject<File>;
  @Input() snapshot: BehaviorSubject<WebcamImage | null> = new BehaviorSubject<WebcamImage | null>(null);
  @Input() isHeadshot?: boolean;
  @Input() uploadMethod?: UploadMethod;

  idUploadState = this.idUploadService.uploadState;
  headshotUploadState = this.headshotUploadService.getState();

  postData = {
    ImageName: '',
    content: ''
  };

  isSuccess(): boolean {
    return this.idUploadState.status === 'success' || this.headshotUploadState.valid_headshot;
  }

  isError(): boolean {
    return this.idUploadState.status === 'error' ||
      this.idUploadState.status === 'rejected' ||
      this.idUploadState.status === 'rejected-passportNumber' ||
      this.headshotUploadState.headshot_upload_error !== '';
  }

  getErrorMessage(): string {
    if (this.isHeadshot) {
      return this.headshotUploadState.headshot_upload_error;
    } else if (this.idUploadState.status === 'rejected-passportNumber') {
      return $localize`Please use a new passport image and try again, or return to the Identity Verification screen and reenter your passport number.`;
    } else if (this.idUploadState.status === 'rejected') {
      return $localize`Please use a new image and try again.`;
    } else {
      return $localize`Please check your network connection and try again.`;
    }
  }

  getSuccessMessage(): string {
    if (this.isHeadshot) {
      return $localize`Your photo upload was successful.`;
    } else {
      return $localize`Your ${this.idUploadService.selectedDocTypeString()} image upload was successful.`;
    }
  }

  isLoading(): boolean {
    return this.headshotUploadState.headshot_uploading || this.idUploadState.id_uploading;
  }

  getOpenStatus(): Observable<boolean> {
    return this.isOpen.asObservable();
  }

  getBase64(file: File, onLoadCallBack: any): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = onLoadCallBack;
    reader.onerror = error => console.error('Error: ', error);
  }

  ngOnInit(): void {
    this.getOpenStatus().subscribe(async (isOpen) => {
      if (isOpen) {
        if (!this.isHeadshot) {
          this.idUploadState.id_uploading = true;
          let idUploadObservable: Observable<void>;
          if(this.uploadMethod === 'FILE') {
            let idFile: File | undefined = this.file?.getValue();
            if(!idFile || idFile.size == 0){
              throw new Error("No file to upload.");
            }
            idUploadObservable = await this.uploadFile(idFile);
          } else if (this.uploadMethod === 'CAMERA') {
            if(this.snapshot.value == null) {
              throw new Error("No image to upload.");
            }
            idUploadObservable = this.uploadWebcamImage(this.snapshot.value);
          } else {
            throw new Error("No upload method selected.");
          }

          idUploadObservable.pipe(
            map(() => this.passThrough()),
            catchError(err => {
              if (err.error?.error_type) {
                this.handleIdUploadError(err.error as IdUploadError);
              } else {
                this.idError();
                throw err;
              }
              return of();
            }),
            first(),
          ).subscribe();

        } else {
          this.headshotUploadState.headshot_uploading = true;
          this.handleSuccessfulHeadshotUpload();
          this.uploadWebcamImage(this.snapshot.value).subscribe({
            next: () => {
              this.handleSuccessfulHeadshotUpload();
            },
            error: (err) => {
              this.headshotRejected(err.status);
            },
            complete: () => {}
          });
        }
      }
    });
  }

  constructor(
    private router: Router,
    private readonly sessionService: SessionService,
    private http: HttpClient,
    private readonly idUploadService: IdUploadService,
    public readonly headshotUploadService: HeadshotUploadService,
    private readonly mteService: MteService,
    private apiService: ApiService
  ) {}

  passThrough(): void {
    this.idUploadState.id_uploading = false;
    this.idUploadState.status = 'success';
    this.idUploadState.submitted = true;
    setTimeout(() => {
      this.resetIdUpload();
      if(this.isHeadshot) {
        this.router.navigate(dashboardRoute);
      } else if(this.idUploadService.idSide.value === 'FRONT'){
        if(((SidesForDocType.get(this.idUploadService.selectedDocType()) ?? 0) > 1)) {
          this.idUploadService.idSide.next('BACK');
          this.snapshot.next(null);
          this.isOpen.next(false);
          this.idUploadService.onRemove();
          this.router.navigate(idUploadRoute);
        } else {
          this.router.navigate(headshotUploadRoute);
        }
      } else {
        this.router.navigate(headshotUploadRoute);
      }
    }, 2000);
  }

  handleSuccessfulHeadshotUpload(): void {
    this.headshotUploadState.valid_headshot = true;
    this.headshotUploadState.headshot_uploading = false;
    this.headshotUploadState.headshot_upload_error = '';
    setTimeout(() => {
      this.resetHeadshot();
      this.router.navigate(dashboardRoute);
    }, 2000);
  }

  resetHeadshot(): void {
    this.headshotUploadState.valid_headshot = false;
    this.headshotUploadState.headshot_uploading = false;
  }

  resetIdUpload(): void {
    this.idUploadState.id_uploading = false;
    this.idUploadState.status = '';
    this.idUploadState.submitted = false;
  }

  showFacialVerification(): void {
    this.idUploadState.id_uploading = false;
  }

  idRejected(badFields: { key: string, value: string}[]): void {
    this.idUploadState.status = !!badFields.find(f => f.key === 'voter_id') ?
      'rejected-passportNumber' : 'rejected';
    this.idUploadState.submitted = true;
    this.idUploadState.id_uploading = false;
  }

  idError(): void {
    this.idUploadState.status = 'error';
    this.idUploadState.submitted = true;
    this.idUploadState.id_uploading = false;
  }

  headshotRejected(statusCode: number): void {
    this.resetHeadshot();
    if (statusCode === 401) {
      this.headshotUploadState.headshot_upload_error = $localize`Please use a new photo and try again.`;
    } else {
      this.headshotUploadState.headshot_upload_error = $localize`Please check your network connection and try again.`;
    }
  }

  async uploadFile(file: File): Promise<Observable<void>> {
    let headers = new HttpHeaders({ 
      'content-type': file.type,
      'x-side': this.idUploadService.idSide.value,
      'x-type': this.idUploadService.selectedDocType(),
      'x-country': "USA"
    })
    if (this.mteService.useMte) {
      return this.apiService.postWithHandshakeCheck<void>(
        idUploadAPI,
        arrayBufferToBase64(await file.arrayBuffer()),
        headers
      ).pipe(map(()=>{}),
        catchError(err => {
          if (err.error?.error_type) {
            this.handleIdUploadError(err.error as IdUploadError);
          } else {
            this.idError();
            throw err;
          }
          return of();
        })
      );
    } else {
      return this.http.post<void>(idUploadAPI, file, 
        {
          headers: headers,
          withCredentials: true
        }
      );
    }
  }

  uploadWebcamImage(photo: WebcamImage | null): Observable<any> {
    if(!photo){
      throw new Error("No photo.");
    }
    const base64Headshot = photo.imageAsDataUrl.replace(/^data:image\/[a-z]+;base64,/, '');
    const blob = this.idUploadService.b64toBlob(base64Headshot, 'image/jpeg');
    let headers = new HttpHeaders().set('content-type', 'image/jpeg');
    if(this.isHeadshot) {
      if(this.mteService.useMte) {
        return this.apiService.postWithHandshakeCheck<any>(
          headshotUploadAPI,
          base64Headshot,
          headers
        );
      } else {
        return this.http.post<HttpResponse<any>>(headshotUploadAPI, blob, { headers, withCredentials: true });
      }
    } else {
      headers = headers.append('x-side', this.idUploadService.idSide.value)
        .append('x-type', this.idUploadService.selectedDocType())
        .append('x-country', "USA");
      if(this.mteService.useMte){
        return this.apiService.postWithHandshakeCheck<any>(
          idUploadAPI,
          base64Headshot,
          headers
        );
      } else {
        return this.http.post<HttpResponse<any>>(idUploadAPI, blob, { headers, withCredentials: true });
      }
    }
  }

  handleIdUploadError(r: IdUploadError): void {
    const rejectionCodes = ['VERIFICATION_NOT_ACCEPTED', 'INVALID_REQUEST_BODY'];
    if (rejectionCodes.includes(r.error_type)) {
      this.idRejected(r.details.reason === 'INVALID_IDENTITY' ? r.details.fields || [] : []);
    } else {
      this.idError();
    }
  }

  clearIdErrors(): void {
    this.idUploadState.status = '';
    this.idUploadState.submitted = false;
  }

  ok(): void {
    this.clearIdErrors();
    this.headshotUploadState.headshot_upload_error = '';
    this.isOpen.next(false);
  }
}